import { Modal } from "antd"
import { useEffect, useState } from "react"

import useGlobalStore from "~/store/global"

export function useChunkErrorHandler() {
  const [isReloading, setIsReloading] = useState(false)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    const hasReloaded = sessionStorage.getItem("chunk_reload_completed")

    const handleError = (event: ErrorEvent) => {
      const isChunkError =
        event.message?.includes("Loading chunk") ||
        event.message?.includes("ChunkLoadError") ||
        event.message?.includes("chunk") ||
        /loading.*chunk/i.test(event.message)

      if (isChunkError && !hasReloaded && !isReloading) {
        event.preventDefault()
        sessionStorage.setItem("chunk_reload_completed", "true")
        setIsReloading(true)
        setShowModal(true)

        setTimeout(() => {
          window.location.reload()
        }, 1500)
      }
    }

    window.addEventListener("error", handleError)

    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason?.message || event.reason || ""

      // Ignore AbortError - these are normal when navigation is cancelled/interrupted
      if (
        event.reason?.name === "AbortError" ||
        reason === "signal is aborted without reason" ||
        reason.includes("aborted") ||
        reason.includes("AbortError")
      ) {
        event.preventDefault()
        return
      }

      const isChunkError =
        reason.includes("Loading chunk") ||
        reason.includes("ChunkLoadError") ||
        reason.includes("chunk") ||
        /loading.*chunk/i.test(reason)

      if (isChunkError && !hasReloaded && !isReloading) {
        event.preventDefault()
        sessionStorage.setItem("chunk_reload_completed", "true")
        setIsReloading(true)
        setShowModal(true)

        setTimeout(() => {
          window.location.reload()
        }, 1500)
      }
    }

    window.addEventListener("unhandledrejection", handleRejection)

    return () => {
      window.removeEventListener("error", handleError)
      window.removeEventListener("unhandledrejection", handleRejection)
    }
  }, [isReloading])

  return { showModal, isReloading }
}

export function ChunkErrorModal() {
  const { showModal, isReloading } = useChunkErrorHandler()
  const isDarkMode = useGlobalStore((store) => store.appTheme === "dark")

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }

    return () => {
      document.body.style.overflow = ""
    }
  }, [showModal])

  return (
    <Modal
      open={showModal}
      closable={false}
      footer={null}
      centered
      maskClosable={false}
      keyboard={false}
      zIndex={9999}
      className="chunk-error-modal"
      styles={{
        content: {
          padding: 0,
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)"
        },
        mask: {
          backdropFilter: "blur(8px)"
        }
      }}
    >
      <div className="relative overflow-hidden">
        <div
          className={`absolute inset-0 bg-gradient-to-br ${
            isDarkMode ? "from-dark-1 to-dark-2" : "from-gray-50 to-gray-100"
          }`}
        />
        <div
          className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-20 animate-pulse ${
            isDarkMode
              ? "bg-gradient-to-br from-indigo-500/30 to-purple-500/30"
              : "bg-gradient-to-br from-blue-400 to-purple-500"
          }`}
        />
        <div
          className={`absolute bottom-0 left-0 w-32 h-32 rounded-full blur-3xl opacity-20 animate-pulse ${
            isDarkMode
              ? "bg-gradient-to-tr from-indigo-500/30 to-blue-500/30"
              : "bg-gradient-to-tr from-indigo-400 to-blue-500"
          }`}
          style={{ animationDelay: "1s" }}
        />
        <div className="relative text-center py-12 px-8">
          <div className="mb-8 relative">
            <div
              className={`inline-flex items-center justify-center w-24 h-24 rounded-full shadow-lg ${
                isDarkMode
                  ? "bg-gradient-to-br from-indigo-900/30 to-purple-900/30"
                  : "bg-gradient-to-br from-indigo-50 to-purple-50"
              }`}
            >
              <div className="relative">
                <svg
                  className={`w-10 h-10 animate-spin ${
                    isDarkMode ? "text-indigo-400" : "text-indigo-500"
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  style={{ animationDuration: "2s" }}
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <div className="absolute inset-0">
                  <div
                    className={`absolute w-2 h-2 rounded-full ${
                      isDarkMode ? "bg-indigo-400" : "bg-indigo-500"
                    }`}
                    style={{
                      top: "0",
                      left: "50%",
                      transform: "translateX(-50%)",
                      animation: "orbit 2s linear infinite"
                    }}
                  ></div>
                </div>
              </div>
            </div>
            <div
              className={`absolute inset-0 rounded-full animate-ping opacity-20 ${
                isDarkMode ? "bg-indigo-500/20" : "bg-indigo-500/10"
              }`}
            ></div>
          </div>

          <h2
            className={`text-2xl font-bold mb-3 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {isReloading ? "Updating Application" : "New Version Available"}
          </h2>
          <p
            className={`mb-6 leading-relaxed ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            {isReloading
              ? "We're refreshing your application with the latest updates. This will only take a moment."
              : "A new version of the application is available. We're preparing it for you."}
          </p>

          <div
            className={`w-full h-1.5 rounded-full overflow-hidden ${
              isDarkMode ? "bg-gray-700" : "bg-gray-200"
            }`}
          >
            <div
              className={`h-full rounded-full animate-loading-bar ${
                isDarkMode
                  ? "bg-gradient-to-r from-indigo-400 to-purple-500"
                  : "bg-gradient-to-r from-indigo-500 to-purple-600"
              }`}
              style={{
                width: "100%",
                transformOrigin: "left"
              }}
            ></div>
          </div>
          <div
            className={`mt-6 text-sm font-medium ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            <span className="inline-flex items-center gap-1">
              Loading
              <span className="flex gap-0.5">
                <span
                  className={`w-1 h-1 rounded-full animate-bounce ${
                    isDarkMode ? "bg-gray-500" : "bg-gray-400"
                  }`}
                  style={{ animationDelay: "0s" }}
                ></span>
                <span
                  className={`w-1 h-1 rounded-full animate-bounce ${
                    isDarkMode ? "bg-gray-500" : "bg-gray-400"
                  }`}
                  style={{ animationDelay: "0.2s" }}
                ></span>
                <span
                  className={`w-1 h-1 rounded-full animate-bounce ${
                    isDarkMode ? "bg-gray-500" : "bg-gray-400"
                  }`}
                  style={{ animationDelay: "0.4s" }}
                ></span>
              </span>
            </span>
          </div>
        </div>

        <div
          className={`absolute bottom-0 left-0 right-0 h-2 ${
            isDarkMode
              ? "bg-gradient-to-r from-indigo-400 to-purple-500"
              : "bg-gradient-to-r from-indigo-500 to-purple-600"
          }`}
        ></div>
      </div>

      <style>{`
        @keyframes orbit {
          from {
            transform: translateX(-50%) rotate(0deg) translateX(16px) rotate(0deg);
          }
          to {
            transform: translateX(-50%) rotate(360deg) translateX(16px) rotate(-360deg);
          }
        }

        @keyframes loading-bar {
          0% {
            transform: scaleX(0);
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: scaleX(1);
            opacity: 0.5;
          }
        }

        .animate-loading-bar {
          animation: loading-bar 1.5s ease-in-out infinite;
        }
      `}</style>
    </Modal>
  )
}
